export const DEFAULT_VIDEO_CONSTRAINTS: MediaStreamConstraints["video"] = {
  width: 1280,
  height: 720,
  frameRate: 24
};

// These are used to store the selected media devices in localStorage
export const SELECTED_AUDIO_INPUT_KEY = "TwilioVideoApp-selectedAudioInput";
export const SELECTED_AUDIO_OUTPUT_KEY = "TwilioVideoApp-selectedAudioOutput";
export const SELECTED_VIDEO_INPUT_KEY = "TwilioVideoApp-selectedVideoInput";

// This is used to store the current background settings in localStorage
export const SELECTED_BACKGROUND_SETTINGS_KEY =
  "TwilioVideoApp-selectedBackgroundSettings";

export const GALLERY_VIEW_ASPECT_RATIO = 9 / 16; // 16:9
export const GALLERY_VIEW_MARGIN = 3;

// Occupation codes as stored by the web API. The value is a number, not a name.
export const OCCUPATION = {
  DOCTOR: 1
} as const;

// Payment terms the professional has to agree to before using the payments area.
export const PAYMENT_TERMS_URL = "https://www.myscribo.com/terms/payments/";

// The same policy, read from the marketing site's WordPress REST API so it can
// be shown in place instead of sending the professional to another tab. Asked
// for by slug rather than by page id, so the page can be recreated without
// silently breaking this, and the answer is matched against the link above.
export const PAYMENT_TERMS_CONTENT_URL =
  "https://myscribo.com/wp-json/wp/v2/pages?slug=payments&_fields=link,content";

// The same policy as a document to keep. Served by this app rather than linked
// from elsewhere: only a same-origin file honours the `download` attribute —
// a cross-origin one opens in a tab instead, which is not what is being asked
// for. The trade-off is that revising it takes a deploy.
export const PAYMENT_TERMS_PDF_URL = "/politica-de-pagamento.pdf";
export const PAYMENT_TERMS_PDF_FILENAME = "Politica de Pagamento - SCRIBO.pdf";

// Public manual for the payment services, linked from the configuration screens.
export const PAYMENTS_MANUAL_URL =
  "https://cdn.kenetto.com/scribo/docs/manual_payment_services.pdf";

// Contextual help and onboarding copy is authored outside the app and served as
// { text: "<html>" }. The HTML is external, so always sanitise before rendering.
export const HELP_CONTENT_BASE_URL = "https://cdn.kenetto.com/scribo/info";

// Identifiers of the help documents used across the payments screens.
export const PAYMENTS_HELP_CONTENT = {
  LIST_CONFIG: "payments_list_config",
  ADD_CONFIG: "payments_add_config",
  UPDATE_CONFIG: "payments_update_config",
  CONFIG_ADD_DOCS: "payments_config_add_docs",
  LIST: "payments_list",
  CREATE_PAYMENT: "payments_create_payment",
  AT_UPDATE: "payments_at_update",
  ADD_LINE: "payments_add_line",
  // These two are full onboarding steps rather than contextual help.
  EXPLANATION: "payments_explanation",
  NEXT_STEPS: "payments_next_steps_explanation"
} as const;

export type PaymentsHelpContentKey = typeof PAYMENTS_HELP_CONTENT[keyof typeof PAYMENTS_HELP_CONTENT];

// Validation rules of the payment configuration form, as enforced by the API.
export const FISCAL_ID_LENGTH = 9;
export const POSTAL_CODE_LENGTH = 8; // "1234-567"
export const IBAN_MIN_LENGTH = 5;
export const IBAN_MAX_LENGTH = 34;
export const MIN_NAME_LENGTH = 3;

// How long the tax id lookup is given before the form gives up on prefilling.
export const MAX_WAIT_TAX_ID_INFO_MS = 500;

// Tax authority credentials must be longer than 3 characters.
export const AT_CREDENTIAL_MIN_LENGTH = 4;

// Length of the UUID Easypay assigns once the account is created. Its presence
// is what tells us the account exists on Easypay's side.
export const EASYPAY_ACCOUNT_ID_LENGTH = 36;

// KYC uploads. The S3 policy carries a content-length-range, so the file is
// checked here first to fail with a clear message instead of an opaque S3 error.
export const KYC_ACCEPTED_FILE_TYPES = "image/*,application/pdf";
export const KYC_MIN_FILE_SIZE_BYTES = 1024; // 1 KB
export const KYC_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

// Sentinel codes of the tax number selector. A negative code means no real tax
// number was chosen, which is what makes the request use the final consumer one.
export const FISCAL_ID_OPTIONS = {
  FINAL_CONSUMER: -100,
  ADD_NEW: -99
} as const;

// Payment line limits enforced by the mobile app and expected by the API.
export const MAX_LINE_QUANTITY_DIGITS = 4;
export const MAX_LINE_UNIT_COST_DIGITS = 9;
